import { defaultLanguage, languages } from '../i18n';
import { UMAMI_WEBSITE_ID } from '../constants/analytics';
import { getCurrentPageLanguage } from './utils';

const UMAMI_READY_POLL_MS = 100;
const UMAMI_READY_TIMEOUT_MS = 5000;
const LANDED_STORAGE_KEY = 'landed';

/**
 * Sends a custom event to Umami.
 *
 * The Umami script is loaded with `defer` and only defines `window.umami` once it
 * runs, so an event fired straight from the first render can arrive before the
 * tracker exists. This waits for it (up to a few seconds) instead of dropping the event.
 *
 * @param name {string} event name as it should appear in the Umami dashboard
 * @param data {Object=} optional event data
 */
export const trackEvent = (name, data) => {
    if (typeof window === 'undefined') return;
    if (process.env.NODE_ENV === 'development') {
        console.debug('[analytics] track', name, data);
    }
    if (!UMAMI_WEBSITE_ID) return;

    const startedAt = Date.now();
    const attempt = () => {
        if (window.umami && typeof window.umami.track === 'function') {
            window.umami.track(name, data);
            return;
        }
        if (Date.now() - startedAt < UMAMI_READY_TIMEOUT_MS) {
            setTimeout(attempt, UMAMI_READY_POLL_MS);
        }
    };
    attempt();
};

const isHomePath = (pathname) => {
    const normalized = pathname.replace(/\/+$/, '');
    if (normalized === '') return true;
    return languages.some(language => language !== defaultLanguage && normalized === `/${language}`);
};

const isMobileDevice = () => {
    const { userAgent, maxTouchPoints, userAgentData } = navigator;
    if (/Android|iPhone|iPad|iPod/i.test(userAgent)) return true;
    // iPadOS Safari reports a desktop (Macintosh) user agent; touch support tells it apart.
    if (/Macintosh/.test(userAgent) && maxTouchPoints > 1) return true;
    return Boolean(userAgentData && userAgentData.mobile);
};

/**
 * Marks this tab's session as having landed on the site and reports whether this
 * is the first page of the session. When storage is unavailable we cannot tell,
 * so we answer "no" rather than count every page load.
 *
 * @return {boolean}
 */
const markLandedAndCheckFirst = () => {
    try {
        const isFirst = !window.sessionStorage.getItem(LANDED_STORAGE_KEY);
        window.sessionStorage.setItem(LANDED_STORAGE_KEY, '1');
        return isFirst;
    } catch (e) {
        return false;
    }
};

/**
 * Heuristic for the printed QR codes, which point to the homepage and carry no
 * tracking parameter. A scan shows up as: first page of the session is the
 * homepage, on a phone, with no referrer. Typed URLs, bookmarks and links opened
 * from native apps look the same, hence "likely".
 *
 * Must run exactly once per full page load (see gatsby-browser.js), on every page,
 * so that a session that started elsewhere and navigated home is not counted.
 */
export const trackLikelyQrEntry = () => {
    if (typeof window === 'undefined') return;

    const firstInSession = markLandedAndCheckFirst();
    if (!firstInSession) return;

    const { pathname } = window.location;
    if (!isHomePath(pathname)) return;
    if (!isMobileDevice()) return;
    if (document.referrer) return;

    trackEvent('likely_qr_entry', { language: getCurrentPageLanguage(pathname) });
};
